import type { FC, HTMLAttributes } from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import type { Placement } from "@react-types/overlays";
import { BookOpen01, ChevronSelectorVertical, LogOut01, Settings01, User01 } from "@untitledui/icons";
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import { RadioButtonBase } from "@/components/base/radio-buttons/radio-buttons";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";
import { useAccount } from "@/providers/account-provider";
import { SignOutConfirmModal } from "../sign-out-confirm-modal";
import { accountService } from "@/data/mock-accounts";

type NavAccountType = {
    /** Unique identifier for the nav item. */
    id: string;
    /** Name of the account holder. */
    name: string;
    /** Email address of the account holder. */
    email: string;
    /** Avatar image URL. */
    avatar: string;
    /** Online status of the account holder. This is used to display the online status indicator. */
    status: "online" | "offline";
    /** User type for display */
    userType?: "SHIPPER" | "RECEIVER" | "ADMIN" | "FREIGHT_FORWARDER";
};

// Genera accounts disponibili per lo switch: un SHIPPER, un RECEIVER, un ADMIN e un FREIGHT_FORWARDER
const getSwitchableAccounts = (): NavAccountType[] => {
    const allAccounts = accountService.getAll();

    // Trova un account per ogni tipo
    const shipperAccount = allAccounts.find(acc => acc.userType === 'SHIPPER' && acc.status === 'Attivo');
    const receiverAccount = allAccounts.find(acc => acc.userType === 'RECEIVER' && acc.status === 'Attivo');
    const adminAccount = allAccounts.find(acc => acc.userType === 'ADMIN' && acc.status === 'Attivo');
    const freightForwarderAccount = allAccounts.find(acc => acc.userType === 'FREIGHT_FORWARDER' && acc.status === 'Attivo');

    const accounts: NavAccountType[] = [];

    // Avatar placeholder URLs
    const avatars = {
        shipper: "https://www.untitledui.com/images/avatars/ethan-valdez?fm=webp&q=80",
        receiver: "https://www.untitledui.com/images/avatars/transparent/zahra-christensen?bg=%23E0E0E0",
        admin: "https://www.untitledui.com/images/avatars/transparent/riley-moore?bg=%23E0E0E0",
        freightForwarder: "https://www.untitledui.com/images/avatars/benedict-doherty?",
    };

    if (shipperAccount) {
        accounts.push({
            id: shipperAccount.id,
            name: shipperAccount.legalName,
            email: shipperAccount.email,
            avatar: avatars.shipper,
            status: "online",
            userType: "SHIPPER",
        });
    }

    if (receiverAccount) {
        accounts.push({
            id: receiverAccount.id,
            name: receiverAccount.legalName,
            email: receiverAccount.email,
            avatar: avatars.receiver,
            status: "online",
            userType: "RECEIVER",
        });
    }

    if (freightForwarderAccount) {
        accounts.push({
            id: freightForwarderAccount.id,
            name: freightForwarderAccount.legalName,
            email: freightForwarderAccount.email,
            avatar: avatars.freightForwarder,
            status: "online",
            userType: "FREIGHT_FORWARDER",
        });
    }

    if (adminAccount) {
        accounts.push({
            id: adminAccount.id,
            name: adminAccount.legalName,
            email: adminAccount.email,
            avatar: avatars.admin,
            status: "online",
            userType: "ADMIN",
        });
    }

    return accounts;
};

export const placeholderAccounts: NavAccountType[] = getSwitchableAccounts();

export const NavAccountMenu = ({
    className,
    selectedAccountId = "john",
    onClose,
    onSignOutClick,
    ...dialogProps
}: AriaDialogProps & {
    className?: string;
    accounts?: NavAccountType[];
    selectedAccountId?: string;
    onClose?: () => void;
    onSignOutClick?: () => void;
}) => {
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { switchAccount } = useAccount();

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);


    return (
        <AriaDialog
            {...dialogProps}
            ref={dialogRef}
            className={cx("w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className)}
        >
            <div className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 py-1.5">
                    <NavAccountCardMenuItem label="View profile" icon={User01} shortcut="⌘K->P" />
                    <NavAccountCardMenuItem label="Account settings" icon={Settings01} shortcut="⌘S" />
                    <NavAccountCardMenuItem label="Documentation" icon={BookOpen01} />
                </div>
                <div className="flex flex-col gap-0.5 border-t border-secondary py-1.5">
                    <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-tertiary">Switch account</div>

                    <div className="flex flex-col gap-0.5 px-1.5">
                        {getSwitchableAccounts().map((account) => (
                            <button
                                key={account.id}
                                onClick={() => {
                                    if (account.id !== selectedAccountId) {
                                        switchAccount(account.id);
                                        onClose?.();
                                    }
                                }}
                                className={cx(
                                    "relative w-full cursor-pointer rounded-md px-2 py-1.5 text-left outline-focus-ring hover:bg-primary_hover focus:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
                                    account.id === selectedAccountId && "bg-primary_hover",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <div className="flex flex-col">
                                        {account.userType && (
                                            <span className="inline-block w-fit max-w-xs text-xs text-tertiary px-1.5 py-0.5 rounded bg-secondary_alt mb-1">
                                                {account.userType}
                                            </span>
                                        )}
                                        <AvatarLabelGroup status="online" size="md" src={account.avatar} title={account.name} subtitle={account.email} />
                                    </div>
                                </div>

                                <RadioButtonBase isSelected={account.id === selectedAccountId} className="absolute top-2 right-2" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="pt-1 pb-1.5">
                <NavAccountCardMenuItem
                    label="Sign out"
                    icon={LogOut01}
                    shortcut="⌥⇧Q"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSignOutClick?.();
                        onClose?.();
                    }}
                />
            </div>
        </AriaDialog>
    );
};

const NavAccountCardMenuItem = ({
    icon: Icon,
    label,
    shortcut,
    ...buttonProps
}: {
    icon?: FC<{ className?: string }>;
    label: string;
    shortcut?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button {...buttonProps} className={cx("group/item w-full cursor-pointer px-1.5 focus:outline-hidden", buttonProps.className)}>
            <div
                className={cx(
                    "flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
                    // Focus styles.
                    "outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
                )}
            >
                <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
                    {Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
                </div>

                {shortcut && (
                    <kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">{shortcut}</kbd>
                )}
            </div>
        </button>
    );
};

export const NavAccountCard = ({
    popoverPlacement,
    selectedAccountId: selectedAccountIdProp,
    items = placeholderAccounts,
}: {
    popoverPlacement?: Placement;
    selectedAccountId?: string;
    items?: NavAccountType[];
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");
    const { selectedAccountId: selectedAccountIdFromHook, signOut } = useAccount();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [showSignOutModal, setShowSignOutModal] = useState(false);

    // Usa la prop se fornita, altrimenti usa l'hook
    const selectedAccountId = selectedAccountIdProp ?? selectedAccountIdFromHook;

    const selectedAccount = useMemo(() => {
        // Cerca prima nei placeholder accounts
        let account = placeholderAccounts.find((account) => account.id === selectedAccountId);

        // Se non trovato, cerca nel service e crea un account type
        if (!account) {
            const realAccount = accountService.getById(selectedAccountId);
            if (realAccount) {
                const avatars = {
                    SHIPPER: "https://www.untitledui.com/images/avatars/ethan-valdez?fm=webp&q=80",
                    RECEIVER: "https://www.untitledui.com/images/avatars/transparent/zahra-christensen?bg=%23E0E0E0",
                    ADMIN: "https://www.untitledui.com/images/avatars/transparent/marcus-finn?bg=%23E0E0E0",
                    FREIGHT_FORWARDER: "https://www.untitledui.com/images/avatars/benedict-doherty?",
                };
                account = {
                    id: realAccount.id,
                    name: realAccount.legalName,
                    email: realAccount.email,
                    avatar: avatars[realAccount.userType] || avatars.SHIPPER,
                    status: "online",
                    userType: realAccount.userType,
                };
            }
        }

        return account;
    }, [selectedAccountId]);

    if (!selectedAccount) {
        console.warn(`Account with ID ${selectedAccountId} not found in <NavAccountCard />`);
        return null;
    }

    return (
        <>
            <div ref={triggerRef} className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
                <AvatarLabelGroup
                    size="md"
                    src={selectedAccount.avatar}
                    title={selectedAccount.name}
                    subtitle={selectedAccount.email}
                    status={selectedAccount.status}
                />

                <div className="absolute top-1.5 right-1.5">
                    <AriaDialogTrigger isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <AriaButton className="flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover">
                            <ChevronSelectorVertical className="size-4 shrink-0" />
                        </AriaButton>
                        <AriaPopover
                            placement={popoverPlacement ?? (isDesktop ? "right bottom" : "top right")}
                            triggerRef={triggerRef}
                            offset={8}
                            className={({ isEntering, isExiting }) =>
                                cx(
                                    "origin-(--trigger-anchor-point) will-change-transform",
                                    isEntering &&
                                    "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                                    isExiting &&
                                    "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                                )
                            }
                        >
                            <NavAccountMenu
                                selectedAccountId={selectedAccountId}
                                accounts={items}
                                onClose={() => setIsPopoverOpen(false)}
                                onSignOutClick={() => setShowSignOutModal(true)}
                            />
                        </AriaPopover>
                    </AriaDialogTrigger>
                </div>
            </div>

            <SignOutConfirmModal
                isOpen={showSignOutModal}
                onClose={() => setShowSignOutModal(false)}
                onConfirm={() => {
                    signOut();
                    setShowSignOutModal(false);
                }}
            />
        </>
    );
};
